import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useMemo,
} from 'react';
import { Check } from 'lucide-react';

import { Accordion } from '@components/primitives';
import { cn } from '@components/primitives/utilities';

import css from './styles.module.css';

type TitleAs = 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface StepsContextValue {
  titleAs: TitleAs;
  collapsible: boolean;
}

const StepsContext = createContext<StepsContextValue | null>(null);

export interface ItemProps extends PropsWithChildren {
  title: string;
  /** Buttons in the title row, before the chevron, when `collapsible` */
  actions?: ReactNode;
  description?: ReactNode;
  /** Identifies the step for `value` and `defaultValue` when `collapsible`. */
  value?: string;
  /** Keeps the content mounted while the step is closed, when `collapsible`. */
  forceMount?: boolean;
  /** Shows a check in place of the number. */
  done?: boolean;
  /** Makes the number a button that marks the step done or not done. */
  onDoneChange?(done: boolean): void;
}

function Item({
  title,
  actions,
  description,
  value,
  forceMount,
  done = false,
  onDoneChange,
  children,
}: ItemProps) {
  const context = useContext(StepsContext);
  if (!context) {
    throw new Error('Steps.Item must be used within a Steps component');
  }

  const Title = context.titleAs;

  const number = onDoneChange ? (
    <button
      type="button"
      className={css.number}
      data-done={done || undefined}
      aria-label={`${title}: done`}
      aria-pressed={done}
      title={done ? 'Mark as not done' : 'Mark as done'}
      onClick={() => onDoneChange(!done)}
    >
      {done && <Check aria-hidden />}
    </button>
  ) : (
    <div className={css.number} data-done={done || undefined}>
      {done && <Check aria-hidden />}
    </div>
  );

  if (context.collapsible) {
    return (
      <Accordion.Item value={value} asChild>
        <li className={css.step}>
          {number}
          <div className={css.content}>
            <div className={css.titleRow}>
              <Accordion.Header asChild>
                <Title className={cn(css.title, css.collapsibleTitle)}>
                  <Accordion.Trigger asChild>
                    <button type="button" className={css.trigger}>
                      {title}
                      {!actions && (
                        <Accordion.Indicator className={css.indicator} />
                      )}
                    </button>
                  </Accordion.Trigger>
                </Title>
              </Accordion.Header>

              {actions && (
                <>
                  <div className={css.actions}>{actions}</div>
                  {/* Outside the trigger so the actions can sit before it.
                      Clicks pass through to the trigger, which covers the row. */}
                  <Accordion.Indicator
                    className={css.rowIndicator}
                    aria-hidden
                  />
                </>
              )}
            </div>

            <Accordion.Content
              className={cn(css.panel, forceMount && css.forcedPanel)}
              forceMount={forceMount || undefined}
            >
              <div className={css.panelRows}>
                <div className={css.panelClip}>
                  <div className={css.panelContent}>
                    {description && (
                      <p className={css.description}>{description}</p>
                    )}
                    {children}
                  </div>
                </div>
              </div>
            </Accordion.Content>
          </div>
        </li>
      </Accordion.Item>
    );
  }

  return (
    <li className={css.step}>
      {number}
      <div className={css.content}>
        <Title className={css.title}>{title}</Title>
        {description && <p className={css.description}>{description}</p>}
        {children}
      </div>
    </li>
  );
}

export interface StepsProps {
  className?: string;
  titleAs: TitleAs;
  /** Shows one step at a time. Each opens from its title, like an accordion. */
  collapsible?: boolean;
  /** The open step's `value`, when `collapsible`. */
  value?: string;
  /** The step that starts open, when `collapsible` and `value` isn't set. */
  defaultValue?: string;
  onValueChange?(value: string): void;
}

function Root({
  children,
  className,
  titleAs,
  collapsible = false,
  value,
  defaultValue,
  onValueChange,
}: PropsWithChildren<StepsProps>) {
  const context = useMemo(
    () => ({ titleAs, collapsible }),
    [titleAs, collapsible]
  );

  const list = <ol className={cn(css.list, className)}>{children}</ol>;

  return (
    <StepsContext.Provider value={context}>
      {collapsible ? (
        <Accordion
          type="single"
          collapsible
          value={value}
          defaultValue={defaultValue}
          onValueChange={onValueChange}
          asChild
        >
          {list}
        </Accordion>
      ) : (
        list
      )}
    </StepsContext.Provider>
  );
}

export const Steps = Object.assign(Root, {
  Item,
});
